import { evaluateSchedule, type ManifestLayout, type PlayerManifest } from '@tbcast/shared';
import { createWidgetRenderable } from '../widgets';
import { createRenderable } from './items';
import { flattenPlaylist, Sequence } from './sequence';
import { runTransition } from './transitions';
import type { PlaybackContext, Renderable, SequenceItem } from './types';

type ContentRef = NonNullable<PlayerManifest['root']>;

/** Something that occupies an area and can be torn down. */
interface Mounted {
  el: HTMLElement;
  destroy(): void;
}

function sequenceFor(ref: { kind: string; id: string }, ctx: PlaybackContext): SequenceItem[] {
  if (ref.kind === 'playlist') return flattenPlaylist(ref.id, ctx);
  if (ref.kind === 'media' || ref.kind === 'presentation' || ref.kind === 'widget') {
    const media = ctx.manifest.media.find((m) => m.id === ref.id);
    if (media && (media.type === 'pdf' || media.type === 'pptx') && media.pageAssetIds?.length) {
      return media.pageAssetIds.map((_, page) => ({
        key: `${ref.id}#${page}`,
        kind: 'media' as const,
        refId: ref.id,
        durationMs: media.durationMs ?? 10_000,
        transition: 'fade' as const,
        page,
      }));
    }
    return [
      {
        key: ref.id,
        kind: ref.kind as SequenceItem['kind'],
        refId: ref.id,
        durationMs: media?.durationMs ?? 10_000,
        transition: 'fade',
      },
    ];
  }
  return [];
}

/** Mounts a playlist / single item as a self-advancing sequence. */
function mountSequence(ref: { kind: string; id: string }, ctx: PlaybackContext): Mounted {
  const el = document.createElement('div');
  el.className = 'tb-layer tb-sequence';
  el.style.visibility = 'visible';
  const items = sequenceFor(ref, ctx);
  if (items.length === 1 && items[0]!.kind === 'widget') {
    // Widgets are long-running: no sequencing needed.
    const r = createRenderable(items[0]!, ctx);
    if (r) {
      el.appendChild(r.el);
      r.el.style.visibility = 'visible';
      void r.ready().then(() => r.play());
    }
    return { el, destroy: () => (r?.destroy(), el.remove()) };
  }
  const seq = new Sequence(el, items, ctx);
  seq.start();
  return { el, destroy: () => (seq.destroy(), el.remove()) };
}

/** Mounts a multi-zone layout scaled to the available area. */
function mountLayout(layout: ManifestLayout, ctx: PlaybackContext): Mounted {
  const el = document.createElement('div');
  el.className = 'tb-layer tb-layout';
  el.style.visibility = 'visible';
  el.style.background = layout.background ?? '#000';
  const children: Array<{ destroy(): void }> = [];
  for (const zone of [...layout.zones].sort((a, b) => a.z - b.z)) {
    const z = document.createElement('div');
    z.className = 'tb-zone';
    z.style.left = `${(zone.x / layout.width) * 100}%`;
    z.style.top = `${(zone.y / layout.height) * 100}%`;
    z.style.width = `${(zone.w / layout.width) * 100}%`;
    z.style.height = `${(zone.h / layout.height) * 100}%`;
    z.style.zIndex = String(zone.z);
    if (zone.background) z.style.background = zone.background;
    el.appendChild(z);
    if (!zone.content) continue;
    if (zone.content.kind === 'widget') {
      const w = ctx.manifest.widgets.find((x) => x.id === zone.content!.refId);
      if (!w) continue;
      const r: Renderable = createWidgetRenderable(w.type, w.config, ctx);
      r.el.style.visibility = 'visible';
      z.appendChild(r.el);
      void r.ready().then(() => r.play());
      children.push(r);
    } else {
      const m = mountSequence({ kind: zone.content.kind, id: zone.content.refId }, ctx);
      z.appendChild(m.el);
      children.push(m);
    }
  }
  return {
    el,
    destroy() {
      for (const c of children) c.destroy();
      el.remove();
    },
  };
}

function mountContent(ref: { kind: string; id: string }, ctx: PlaybackContext): Mounted | null {
  if (ref.kind === 'layout') {
    const l = ctx.manifest.layouts.find((x) => x.id === ref.id);
    return l ? mountLayout(l, ctx) : null;
  }
  return mountSequence(ref, ctx);
}

/**
 * Top-level content controller. Resolves schedules (evaluated locally in the
 * screen's time zone every 15 s) and cross-fades when the active content changes.
 */
export class RootPlayer {
  private mounted: Mounted | null = null;
  private activeKey: string | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;

  constructor(
    private container: HTMLElement,
    private ctx: PlaybackContext,
  ) {}

  /** Content that should be on screen right now. */
  resolve(now = new Date()): { kind: string; id: string } | null {
    const root = this.ctx.manifest.root;
    if (!root) return null;
    if (root.kind !== 'schedule') return root;
    const s = this.ctx.manifest.schedules.find((x) => x.id === root.id);
    if (!s) return null;
    return evaluateSchedule(s.rules, now, this.ctx.timeZone, s.fallback).content;
  }

  start(): void {
    this.tick();
    this.timer = setInterval(() => this.tick(), 15_000);
  }

  /** Swaps in a new manifest; keeps playing if the active content is unchanged in structure. */
  update(ctx: PlaybackContext): void {
    this.ctx = ctx;
    this.activeKey = null; // force remount with the new content
    this.tick();
  }

  get currentRef(): string | null {
    return this.activeKey;
  }

  private tick(): void {
    const ref = this.resolve();
    const key = ref ? `${ref.kind}:${ref.id}` : 'idle';
    if (key === this.activeKey) return;
    this.activeKey = key;
    const prev = this.mounted;
    const next = ref ? mountContent(ref as ContentRef, this.ctx) : null;
    if (next) {
      next.el.style.visibility = 'hidden';
      this.container.appendChild(next.el);
      void runTransition(next.el, prev?.el ?? null, 'fade', 600).then(() => prev?.destroy());
    } else {
      prev?.destroy();
    }
    this.mounted = next;
    this.container.dataset.empty = next ? 'false' : 'true';
  }

  destroy(): void {
    if (this.timer) clearInterval(this.timer);
    this.mounted?.destroy();
    this.mounted = null;
  }
}
